import Verified from 'pages/auth/verified'
import { MobileDealsPage } from 'pages/app/deals/mobile'
import { MobileContactsPage } from 'pages/app/contacts/mobile'
import Home from 'pages/app/mobile/home'
import SearchPage from 'pages/app/mobile/search'
import { MobileAppLayout } from 'pages/app/mobile/layout'
import MobileNotFound from 'pages/app/mobile/mobile-not-found'
import Failed from 'pages/auth/failed'
import { MobileAuthLayout } from 'pages/auth/layout/mobile'
import Login from 'pages/auth/login'
import Register from 'pages/auth/register'
import ResetPassword from 'pages/auth/reset-password'
import ResetPasswordConfirm from 'pages/auth/reset-password-confirm'
import SSOCallback from 'pages/auth/sso-callback'
import SSOLogin from 'pages/auth/sso-login'
import { MobileCommonLayout } from 'pages/common/layout/mobile'
import Privacy from 'pages/common/privacy'
import Terms from 'pages/common/terms'
import Oops from 'pages/oops'
import { Outlet, RouterProvider } from 'react-router-dom'
import { sentryCreateBrowserRouter } from 'services/sentry'
import { usePostHogPageview } from 'hooks/usePostHogPageview'
import { PageWithTitle } from 'utils/PageWithTitle'
import ProtectedRoute from 'utils/ProtectedRoute'
import { MobileDealsCreate } from 'pages/app/deals/MobileDealsCreate'
import MobileContactsCreate from 'pages/app/contacts/mobile/MobileContactsCreate'
import MobileCompanyCreate from 'pages/app/companies/mobile/MobileCompanyCreate'
import { MobileSamplesPage } from 'pages/app/samples/mobile'
import { MobileProductsPage } from 'pages/app/products/mobile'
import { MobileSampleDetailPage } from 'pages/app/samples/MobileSampleDetailPage'
import { MobileCreateSamplePage } from 'pages/app/samples/MobileCreateSamplePage'
import { MobileSamplesNeedsReviewRedirect } from 'pages/app/samples/MobileSamplesNeedsReviewRedirect'
import { MobileRemindersPage } from 'pages/app/reminders/mobile'
import { MobileCreateReminderPage } from 'pages/app/reminders/MobileCreateReminderPage'
import { MobileNotesPage } from 'pages/app/notes/mobile'
import { MobileCreateNotePage } from 'pages/app/notes/MobileCreateNotePage'
import { MobileEditNotePage } from 'pages/app/notes/MobileEditNotePage'
import { MobileDeleteNotePage } from 'pages/app/notes/MobileDeleteNodePage'
import { MobileChainPage } from 'pages/app/new-company-pages/MobileChainPage'
import { MobileUniversityPage } from 'pages/app/new-company-pages/MobileUniversityPage'
import { MobileK12Page } from 'pages/app/new-company-pages/MobileK12Page'
import { MobileHospitalPage } from 'pages/app/new-company-pages/MobileHospitalPage'
import { MobileNursingHomePage } from 'pages/app/new-company-pages/MobileNursingHomePage'
import { MobileLodgingPage } from 'pages/app/new-company-pages/MobileLodgingPage'
import { MobileConveniencePage } from 'pages/app/new-company-pages/MobileConveniencePage'
import { MobileDoorsListPage } from 'pages/app/new-company-pages/MobileDoorsListPage'
import { MobileDoorPage } from 'pages/app/new-company-pages/MobileDoorPage'
import { MobileAccountAttributesPage } from 'components/MobileCompanyPage/MobileAccountAttributes'
import { MobileEventsPage } from 'pages/app/events/mobile'
import { MobileMenuPage } from 'pages/app/menu/mobile'
import { MobileEditReminderPage } from 'pages/app/reminders/MobileEditReminderPage'

import { MobileEmailsPage } from 'components/MobileCompanyPage/MobileEmail/MobileViewEmailsPage'
import { MobileSuggestionPage } from 'components/MobileCompanyPage/MobileSuggestionPage'
import { MobileCustomCompanyPage } from 'pages/app/new-company-pages/MobileCustomCompanyPage'
import { MobileCustomDoorPage } from 'pages/app/new-company-pages/MobileCustomDoorPage'
import MobileCompanyEdit from 'pages/app/companies/mobile/MobileCompanyEdit'
// Root layout that wraps all routes to track pageviews
function RootLayout() {
  usePostHogPageview()
  return <Outlet />
}

export default function Mobile() {
  const appRouter = sentryCreateBrowserRouter(
    [
      {
        // Root wrapper for PostHog pageview tracking
        element: <RootLayout />,
        children: [
          {
            path: '*',
            element: (
              <ProtectedRoute>
                <MobileNotFound />
              </ProtectedRoute>
            ),
          },
          {
            path: '/',
            element: <MobileAppLayout />,
            errorElement: <Oops />,
            children: [
              {
                path: '',
                element: (
                  <ProtectedRoute>
                    <Home />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'chain/:chain_name',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Dashboard">
                      <MobileChainPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'chain/:chain_name/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'chain/:chain_name/doors',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Doors">
                      <MobileDoorsListPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              // ── Non-restaurant First Bite company types (mirror desktop paths
              // so type-aware search navigation resolves on mobile). Each has an
              // account-attributes child route for the summary's "view all" nav,
              // which reads its company id from navigation state. ──
              {
                path: 'university/:university_name',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="University">
                      <MobileUniversityPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'university/:university_name/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'k12/:k12_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="K12 District">
                      <MobileK12Page />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'k12/:k12_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'hospital/:hospital_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Hospital">
                      <MobileHospitalPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'hospital/:hospital_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'nursinghome/:nursinghome_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Senior Living">
                      <MobileNursingHomePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'nursinghome/:nursinghome_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'lodging/chain/:lodging_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Lodging & Hospitality">
                      <MobileLodgingPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'lodging/chain/:lodging_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'convenience/chain/:convenience_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Convenience Store">
                      <MobileConveniencePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'convenience/chain/:convenience_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'restaurant/:door_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Restaurant">
                      <MobileDoorPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'company/:company_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Company">
                      <MobileCustomCompanyPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'company/:company_id/edit',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Edit Company">
                      <MobileCompanyEdit />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'company/:company_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'door/:door_proxy_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Door">
                      <MobileCustomDoorPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'deals',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Deals">
                      <MobileDealsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'deals/create',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Deals">
                      <MobileDealsCreate />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'contacts',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Contacts">
                      <MobileContactsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'contacts/create',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Contacts">
                      <MobileContactsCreate />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'companies/create',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Create Company">
                      <MobileCompanyCreate />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                // `Limited` (broker) users can access products on desktop
                // (see desktop.tsx) and need spec-sheet access in the field
                // too (FIR-7497 AC3), so mirror that minimum role here.
                path: 'products',
                element: (
                  <ProtectedRoute minimumRole="Limited">
                    <PageWithTitle title="Products">
                      <MobileProductsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'samples/needs-review',
                element: (
                  <ProtectedRoute>
                    <MobileSamplesNeedsReviewRedirect />
                  </ProtectedRoute>
                ),
              },
              {
                path: 'samples',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Samples">
                      <MobileSamplesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'samples/:sample_id',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Sample Details">
                      <MobileSampleDetailPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'samples/create',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Create a Sample">
                      <MobileCreateSamplePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reminders',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Reminders">
                      <MobileRemindersPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reminders/create',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Create a Reminder">
                      <MobileCreateReminderPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'reminders/:reminder_id/edit',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Edit Reminder">
                      <MobileEditReminderPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'notes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Notes">
                      <MobileNotesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'notes/:note_id/edit',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Edit Note">
                      <MobileEditNotePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'notes/:note_id/delete',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Delete Note">
                      <MobileDeleteNotePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'events',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Events">
                      <MobileEventsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'menu',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Menu">
                      <MobileMenuPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'search',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Search">
                      <SearchPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'restaurant/:door_id/account-attributes',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Account Attributes">
                      <MobileAccountAttributesPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'notes/create',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Create Note">
                      <MobileCreateNotePage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'chain/:chain_name/suggest-edit',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Suggest an Edit">
                      <MobileSuggestionPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'restaurant/:door_id/suggest-edit',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Suggest an Edit">
                      <MobileSuggestionPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'chain/:chain_name/emails',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Emails">
                      <MobileEmailsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                // Type-agnostic emails route — MobileEmailsPage reads
                // contact_company_id from the query string, so every company
                // type's shell email action can target it.
                path: 'emails',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Emails">
                      <MobileEmailsPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
              {
                path: 'door/:door_id/suggest-edit',
                element: (
                  <ProtectedRoute>
                    <PageWithTitle title="Suggest an Edit">
                      <MobileSuggestionPage />
                    </PageWithTitle>
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: '/login',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Log-in">
                    <Login />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/signup',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Sign-up">
                    <Register />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/sso-login',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="SSO Login">
                    <SSOLogin />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/auth/sso-callback',
            element: (
              <PageWithTitle title="SSO Authentication">
                <SSOCallback />
              </PageWithTitle>
            ),
          },
          {
            path: '/email-verified-confirmation',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Email Verified">
                    <Verified />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/email-verification-failed',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Email Verification Failed">
                    <Failed />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/reset-password',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Reset Password">
                    <ResetPassword />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/password-reset-confirm/:uid/:token',
            element: <MobileAuthLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Reset Password Confirmation">
                    <ResetPasswordConfirm />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/terms-of-use',
            element: <MobileCommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Terms of Use">
                    <Terms />
                  </PageWithTitle>
                ),
              },
            ],
          },
          {
            path: '/privacy-policy',
            element: <MobileCommonLayout />,
            children: [
              {
                path: '',
                element: (
                  <PageWithTitle title="Privacy Policy">
                    <Privacy />
                  </PageWithTitle>
                ),
              },
            ],
          },
        ],
      },
    ],
    { basename: import.meta.env.BASE_URL }
  )

  return <RouterProvider router={appRouter} />
}
